import type {
  OnboardingResponseData,
  OnboardingStep,
  ProgramNameWire,
  ProgramStatusWire,
  ProgramStepNameWire,
  ProgramStepStatusWire
} from '@/shared/api/brandPrograms';
import { NEWSROOM_V3_ENABLED } from '@/shared/featureFlags';
import { APP_ROUTES, APP_SEGMENTS } from '@/shared/routes/paths';
import type { BrandFeatureFlag } from '@/shared/types/brand';
import type { FeatureKey } from '@/shared/types/features';

/**
 * The single description of the v3 (PROGRAMS_ROADMAP_V3) programs.
 *
 * Every surface that decides what a program step is, where it lives, or who
 * may open it reads this module: the route tree (src/routes/index.tsx), the
 * roadmap strip (ui/ProgramRoadmap), the Programs tiles and the editors' step
 * submits. Before this existed the same mapping was re-encoded in five places
 * and each copy applied a slightly different set of gates.
 *
 * Kept icon-free and page-free, like pageRegistry: it is imported by the route
 * tree and by shared components, and must not drag page bundles along.
 */

/** Which page renders an editor step. The route tree maps keys to lazy pages. */
export type ProgramEditorKey = 'cleanout-landing' | 'cashout-tile' | 'newsroom';

export interface ProgramEditorDefinition {
  key: ProgramEditorKey;
  /** The editor's own, bookmarkable URL. */
  canonicalPath: string;
  /**
   * The org feature the editor page requires. The canonical route and every
   * step route that renders this editor guard on this same key.
   */
  feature: FeatureKey;
  /**
   * Per-brand flag the editor page also checks in-page (see Brand). The route
   * stays org-wide, as on main; actions that open the editor read this to
   * disable themselves for a brand that does not have it.
   */
  brandFlag?: BrandFeatureFlag;
}

export const PROGRAM_EDITORS = {
  'cleanout-landing': {
    key: 'cleanout-landing',
    canonicalPath: APP_ROUTES.CLEANOUT_LANDING_BUILDER,
    feature: 'CLEAN_OUT'
  },
  'cashout-tile': {
    key: 'cashout-tile',
    canonicalPath: APP_ROUTES.CASHOUT_TILE,
    feature: 'CASHOUT_TILE',
    brandFlag: 'cashoutTile'
  },
  newsroom: {
    key: 'newsroom',
    canonicalPath: APP_ROUTES.NEWSROOM,
    feature: 'CLEAN_OUT'
  }
} as const satisfies Record<ProgramEditorKey, ProgramEditorDefinition>;

/**
 * - `owned`   — the partner writes it (PATCH draft / POST submit). Mirrors
 *   OWNED_STEPS_BY_PROGRAM in b2b-api src/validators/programSteps.validator.ts.
 * - `derived` — read-only; its status is computed server-side from an
 *   org-level source (tax documents, contract).
 * - `preview` — rolling out: owned once the server's roadmap includes it.
 *   Until then the strip shows it as not started (only while its build flag
 *   is on) and the portal never writes it, since b2b-api would refuse.
 */
export type ProgramStepOwnership = 'owned' | 'derived' | 'preview';

interface ProgramStepBase {
  name: ProgramStepNameWire;
  label: string;
  description: string;
  /** URL segment under /app/programs/:slug/steps/. */
  segment: string;
  ownership: ProgramStepOwnership;
  /** Build-time rollout gate; a disabled step has no chip and no route. */
  enabled?: boolean;
}

export type ProgramStepDefinition =
  | (ProgramStepBase & {
      /** Renders a full editor page in place at the step URL. */
      surface: 'editor';
      editor: ProgramEditorKey;
      /** Older slugs that still resolve, so existing bookmarks keep working. */
      aliases?: readonly string[];
    })
  | (ProgramStepBase & {
      /**
       * Opens a modal from the roadmap strip. Its step URL redirects to the
       * program's home step, where the strip (and so the modal) lives.
       */
      surface: 'modal';
    });

export interface ProgramDefinition {
  name: ProgramNameWire;
  /** Lower-case URL form of `name`. Route matching is case-insensitive. */
  slug: string;
  label: string;
  /** The org entitlement for the program itself. */
  feature: FeatureKey;
  /** Where Setup / Continue setup lands, and where modal-step URLs redirect. */
  homeStep: ProgramStepNameWire;
  steps: readonly ProgramStepDefinition[];
}

const SHARE_TAX_INFO: ProgramStepDefinition = {
  name: 'SHARE_TAX_INFO',
  label: 'Tax info',
  description: 'Upload your W-9 or W-8 tax form for review.',
  segment: 'tax-info',
  ownership: 'derived',
  surface: 'modal'
};

const SIGN_CONTRACT: ProgramStepDefinition = {
  name: 'SIGN_CONTRACT',
  label: 'Contract',
  description: 'Review and sign your ThredUp partner agreement.',
  segment: 'contract',
  ownership: 'derived',
  surface: 'modal'
};

const GO_LIVE: ProgramStepDefinition = {
  name: 'GO_LIVE',
  label: 'Go live',
  description: 'Pick the date the program launches to your customers.',
  segment: 'go-live',
  ownership: 'owned',
  surface: 'modal'
};

export const V3_PROGRAMS: Record<ProgramNameWire, ProgramDefinition> = {
  CLEAN_OUT: {
    name: 'CLEAN_OUT',
    slug: 'clean_out',
    label: 'Clean Out',
    feature: 'CLEAN_OUT',
    homeStep: 'BUILD_LANDING_PAGE',
    steps: [
      {
        name: 'BUILD_LANDING_PAGE',
        label: 'Landing page',
        description: 'Design and publish your Clean Out landing page.',
        segment: 'landing-page',
        aliases: ['build-landing'],
        ownership: 'owned',
        surface: 'editor',
        editor: 'cleanout-landing'
      },
      {
        name: 'PUBLISH_NEWSROOM',
        label: 'Newsroom',
        description:
          'Publish your Newsroom article and Instagram launch content.',
        segment: 'newsroom',
        ownership: 'preview',
        enabled: NEWSROOM_V3_ENABLED,
        surface: 'editor',
        editor: 'newsroom'
      },
      SHARE_TAX_INFO,
      SIGN_CONTRACT,
      GO_LIVE
    ]
  },
  CASH_OUT: {
    name: 'CASH_OUT',
    slug: 'cash_out',
    label: 'Cash Out',
    feature: 'CASH_OUT',
    homeStep: 'EDIT_CASHOUT_TILE',
    steps: [
      {
        name: 'EDIT_CASHOUT_TILE',
        label: 'Cashout tile',
        description: 'Edit the Cash Out tile shown to your shoppers.',
        segment: 'edit-tile',
        ownership: 'owned',
        surface: 'editor',
        editor: 'cashout-tile'
      },
      SHARE_TAX_INFO,
      SIGN_CONTRACT,
      GO_LIVE
    ]
  }
};

export const V3_PROGRAM_LIST: readonly ProgramDefinition[] = [
  V3_PROGRAMS.CLEAN_OUT,
  V3_PROGRAMS.CASH_OUT
];

export const getProgramStep = (
  program: ProgramNameWire,
  stepName: ProgramStepNameWire
): ProgramStepDefinition | undefined =>
  V3_PROGRAMS[program].steps.find((s) => s.name === stepName);

const stepPathFor = (
  program: ProgramDefinition,
  step: Pick<ProgramStepDefinition, 'segment'>
): string => `${APP_ROUTES.PROGRAMS}/${program.slug}/steps/${step.segment}`;

/** Canonical absolute URL of a step. Throws on a step the program lacks. */
export const stepPath = (
  program: ProgramNameWire,
  stepName: ProgramStepNameWire
): string => {
  const step = getProgramStep(program, stepName);
  if (!step) {
    throw new Error(`${stepName} is not a step of ${program}`);
  }
  return stepPathFor(V3_PROGRAMS[program], step);
};

/** Where Setup / Continue setup lands for a program. */
export const programHomePath = (program: ProgramNameWire): string =>
  stepPath(program, V3_PROGRAMS[program].homeStep);

const isEnabled = (step: ProgramStepDefinition): boolean =>
  step.enabled !== false;

const stepSegments = (step: ProgramStepDefinition): string[] => [
  step.segment,
  ...(step.surface === 'editor' ? (step.aliases ?? []) : [])
];

/**
 * Every URL a step chip counts as "here" for: the step URL, its aliases and,
 * for an editor step, the editor's canonical URL, so the chip highlights
 * whichever way the partner arrived.
 */
export const stepActivePaths = (
  program: ProgramNameWire,
  stepName: ProgramStepNameWire
): string[] => {
  const step = getProgramStep(program, stepName);
  if (!step) return [];
  const paths = stepSegments(step).map((segment) =>
    stepPathFor(V3_PROGRAMS[program], { segment })
  );
  if (step.surface === 'editor') {
    paths.push(PROGRAM_EDITORS[step.editor].canonicalPath);
  }
  return paths;
};

/**
 * Every feature a partner needs to open a step URL, AND-ed: the module gate,
 * the rollout flag, the program entitlement and — for an editor step — the
 * editor's own gate, so a step URL can never be a way round the gate on the
 * editor's canonical route.
 */
export const stepRequiredFeatures = (
  program: ProgramNameWire,
  step: ProgramStepDefinition
): FeatureKey[] => {
  const keys: FeatureKey[] = [
    'PROGRAMS',
    'PROGRAMS_ROADMAP_V3',
    V3_PROGRAMS[program].feature
  ];
  if (step.surface === 'editor')
    keys.push(PROGRAM_EDITORS[step.editor].feature);
  return keys.filter((key, i) => keys.indexOf(key) === i);
};

export interface ProgramStepRoute {
  /** Route path relative to the /app mount. */
  path: string;
  program: ProgramNameWire;
  step: ProgramStepDefinition;
}

/** Every routable step URL, segments and aliases. */
export const PROGRAM_STEP_ROUTES: readonly ProgramStepRoute[] =
  V3_PROGRAM_LIST.reduce<ProgramStepRoute[]>(
    (routes, program) =>
      program.steps.filter(isEnabled).reduce(
        (acc, step) =>
          acc.concat(
            stepSegments(step).map((segment) => ({
              path: `${APP_SEGMENTS.PROGRAMS}/${program.slug}/steps/${segment}`,
              program: program.name,
              step
            }))
          ),
        routes
      ),
    []
  );

/**
 * Query param a modal step's URL redirects with, so the program home opens
 * that step's modal: /steps/go-live → /steps/landing-page?step=go-live.
 */
export const ROADMAP_STEP_PARAM = 'step';

/** Catches any other step URL: unknown segment or wrong program/step pair. */
export const PROGRAM_STEPS_FALLBACK_PATH = `${APP_SEGMENTS.PROGRAMS}/:programSlug/steps/*`;

/**
 * The steps the strip renders, in registry order (which mirrors b2b-api's
 * STEP_SETS). Server steps the program doesn't know are dropped; an enabled
 * `preview` step the server doesn't serve yet is shown as not started.
 */
export const visibleRoadmapSteps = (
  program: ProgramNameWire,
  steps: readonly OnboardingStep[]
): OnboardingStep[] =>
  V3_PROGRAMS[program].steps
    .filter(isEnabled)
    .reduce<OnboardingStep[]>((acc, def) => {
      const served = steps.find((s) => s.stepName === def.name);
      if (served) return acc.concat(served);
      if (def.ownership === 'preview') {
        return acc.concat({
          stepName: def.name,
          status: 'DRAFT',
          reviewerNote: null
        });
      }
      return acc;
    }, []);

/**
 * Whether the setup roadmap shows at all.
 *
 * - Hidden once every step is approved (PO decision #5).
 * - Hidden on a live program (PO decision #7: no lingering setup chrome) —
 *   unless RaaS bounced a step back. The API lets a partner rework a step on a
 *   live program, and the strip is the only place its reviewer note shows.
 * - Hidden while paused: the API refuses every step write until resumed.
 */
export const shouldShowRoadmap = (
  roadmap: Pick<OnboardingResponseData, 'allApproved' | 'programStatus'>,
  visibleSteps: readonly OnboardingStep[]
): boolean => {
  if (visibleSteps.length === 0) return false;
  switch (roadmap.programStatus) {
    case 'PAUSED':
      return false;
    case 'ACTIVE':
      return visibleSteps.some((s) => s.status === 'CHANGES_REQUESTED');
    default:
      return !roadmap.allApproved;
  }
};

// Mirrors of b2b-api src/services/brandPrograms/steps.service.ts. A write
// outside these statuses is refused with a 409, so the portal must not offer
// or fire it.
const PROGRAM_WRITABLE_STATUSES: readonly ProgramStatusWire[] = [
  'ONBOARDING',
  'ACTIVE'
];
const PARTNER_WRITABLE_STEP_STATUSES: readonly ProgramStepStatusWire[] = [
  'DRAFT',
  'CHANGES_REQUESTED'
];

/**
 * Whether the partner owns a step on this roadmap. A `preview` step counts
 * only once the server serves it, so pass the server's steps, never the
 * output of visibleRoadmapSteps (its placeholder would read as served).
 */
export const isOwnedStep = (
  roadmap: Pick<OnboardingResponseData, 'programName' | 'steps'>,
  stepName: ProgramStepNameWire
): boolean => {
  switch (getProgramStep(roadmap.programName, stepName)?.ownership) {
    case 'owned':
      return true;
    case 'preview':
      return roadmap.steps.some((s) => s.stepName === stepName);
    default:
      return false;
  }
};

export type StepWriteBlock =
  | 'not-owned'
  | 'program-not-writable'
  | 'in-review'
  | 'approved';

/**
 * Why the partner cannot write a step right now, or null when they can. A step
 * with no row yet is writable — the first submit creates it.
 */
export const stepWriteBlock = (
  roadmap: Pick<
    OnboardingResponseData,
    'programName' | 'programStatus' | 'steps'
  >,
  stepName: ProgramStepNameWire
): StepWriteBlock | null => {
  if (!isOwnedStep(roadmap, stepName)) return 'not-owned';
  if (!PROGRAM_WRITABLE_STATUSES.includes(roadmap.programStatus)) {
    return 'program-not-writable';
  }
  const status = roadmap.steps.find((s) => s.stepName === stepName)?.status;
  if (status === undefined || PARTNER_WRITABLE_STEP_STATUSES.includes(status)) {
    return null;
  }
  return status === 'APPROVED' ? 'approved' : 'in-review';
};

export const canPartnerWriteStep = (
  roadmap: Pick<
    OnboardingResponseData,
    'programName' | 'programStatus' | 'steps'
  >,
  stepName: ProgramStepNameWire
): boolean => stepWriteBlock(roadmap, stepName) === null;
